import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import type { ServiceOption } from '../../CreateAvailabilityDialog';

interface ServicesStepProps {
  services: ServiceOption[];
  selectedServiceIds: number[];
  onToggleService: (id: number) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export function ServicesStep({
  services,
  selectedServiceIds,
  onToggleService,
  onSelectAll,
  onDeselectAll,
}: ServicesStepProps) {
  const { t } = useTranslation('staff');

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <Label variant="section">
            {t('availability.dialog.services', 'Services')}
          </Label>
          <p className="text-xs text-gray-500">
            {t(
              'availability.wizard.hints.services',
              'Select which services can be booked during this availability.'
            )}{' '}
            {t(
              'availability.dialog.servicesHint',
              'Only services assigned to you are shown. Admins can change your assigned services in Team settings.'
            )}
          </p>
        </div>
        {selectedServiceIds.length > 0 && (
          <span className="text-sm text-blue-600 font-medium whitespace-nowrap">
            {t('availability.dialog.selectedServices', '{{count}} selected', {
              count: selectedServiceIds.length,
            })}
          </span>
        )}
      </div>

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onSelectAll}
        >
          {t('availability.wizard.selectAll', 'Select All')}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onDeselectAll}
        >
          {t('availability.wizard.deselectAll', 'Deselect All')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {services.map((service) => {
          const isSelected = selectedServiceIds.includes(service.id);

          return (
            <label
              key={service.id}
              htmlFor={`service-${service.id}`}
              className={cn(
                'flex items-center gap-3 p-4 rounded-lg border-2 cursor-pointer transition-all tap-highlight-transparent',
                'min-h-[56px] active:scale-[0.98]',
                isSelected
                  ? 'bg-blue-50 border-blue-300'
                  : 'bg-white border-gray-200 hover:border-gray-300 active:bg-gray-50'
              )}
            >
              <Checkbox
                id={`service-${service.id}`}
                checked={isSelected}
                onCheckedChange={() => onToggleService(service.id)}
                className="w-5 h-5"
              />
              <span
                className={cn(
                  'text-base',
                  isSelected ? 'text-blue-900 font-medium' : 'text-gray-700'
                )}
              >
                {service.name}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
